/**
 * Turn anything a FastAPI error body can hold into a STRING safe to render.
 *
 * WHY THIS FILE EXISTS. `setError(err.detail)` is the idiom throughout this app,
 * and `detail` is not always a string. Two of our own handlers answer with an
 * object:
 *
 *   app/core/errors.py  input_validation_exception_handler
 *       {"detail": {error_code, error, message, field}}
 *   FastAPI's own request validation
 *       {"detail": [{loc, msg, type}, ...]}
 *
 * React 18 throws "Objects are not valid as a React child" the moment one of
 * those reaches JSX, and there is NO ErrorBoundary anywhere in this app, so the
 * whole page unmounts to a blank white screen with no message and no recovery
 * but a reload.
 *
 * Found on 2026-09-24 on three live, routed screens at once: /admin/profiles
 * (create a sys-admin with a short password), /admin/users (invite with a blank
 * or malformed email) and the Settings invite form. All three use a modal with a
 * plain onClick rather than a form submit, so the inputs' own `required` and
 * `type=email` attributes never run and the server validator is the FIRST thing
 * that sees the value -- i.e. the object path is the NORMAL path for a typo, not
 * an edge case.
 *
 * ALWAYS RETURNS A STRING. That is the contract, and it is what lets a call site
 * keep the `setError(...)` shape it already had.
 */

/** The first human-readable string inside a FastAPI validation array. */
function fromArray(detail) {
  const withMsg = detail.find((d) => d && typeof d.msg === 'string' && d.msg);
  if (withMsg) return withMsg.msg;
  const plain = detail.find((d) => typeof d === 'string' && d);
  return plain || null;
}

/** The first human-readable string inside an object detail. */
function fromObject(detail) {
  // `message` before `error`: our InputValidationError puts the sentence in
  // `message` and a machine code in `error`, and showing the code to a person
  // is how "input_validation_error" ends up in a UI.
  const inner = detail.message || detail.error || detail.detail;
  return typeof inner === 'string' && inner ? inner : null;
}

/**
 * @param {*} body     the parsed response body (or anything at all)
 * @param {string} fallback  shown when nothing readable can be found
 * @returns {string}
 */
export function errorMessage(body, fallback = 'Request failed') {
  if (typeof body === 'string' && body) return body;

  const detail = body && typeof body === 'object' ? body.detail : undefined;

  if (Array.isArray(detail)) return fromArray(detail) || fallback;
  if (detail && typeof detail === 'object') return fromObject(detail) || fallback;
  if (typeof detail === 'string' && detail) return detail;

  // Some handlers answer {message: ...} with no `detail` wrapper at all.
  if (body && typeof body === 'object') {
    const top = fromObject(body);
    if (top) return top;
  }
  return fallback;
}

/**
 * Belt and braces for a render site: guarantees a renderable child even if a
 * value reached state without going through `errorMessage` above.
 *
 * Deliberately NOT a substitute for it -- `String({})` is "[object Object]",
 * which is useless to a person. This exists so a missed call site degrades to an
 * ugly message instead of a blank page.
 */
export function renderable(value, fallback = '') {
  if (value === null || value === undefined || value === false) return fallback;
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  return errorMessage(value, fallback);
}

export default errorMessage;
